import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import SectionHeading from 'components/SectionHeading';
import SectionSubtitle from 'components/SectionSubtitle';
import titleUnderline from '../OnlineRepairs/0707.png';
import styles from './FAQSection.module.css';

const FAQ_ITEM_IDS = [1, 2, 3, 4, 5, 6, 7] as const;


function PlusIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="none"
      stroke="#fff"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'block', overflow: 'visible' }}
      aria-hidden
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

interface FAQSectionProps {
  /** 'product' = bar-style items on light background (e.g. TypeStep); 'homepage' = default */
  variant?: 'homepage' | 'product';
}

export default function FAQSection({ variant = 'homepage' }: FAQSectionProps) {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<number | null>(null);
  const isProductVariant = variant === 'product';

  const toggle = (id: number) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  return (
    <section
      className={isProductVariant ? `${styles.section} ${styles.sectionProduct}` : styles.section}
      aria-labelledby="faq-heading"
    >
      <div className={styles.container}>
        <header className={isProductVariant ? `${styles.header} ${styles.headerProduct}` : styles.header}>
          <div className={styles.titleWrapper}>
            <SectionHeading id="faq-heading" size="large" className={styles.title}>
              FAQ
            </SectionHeading>
            {!isProductVariant && (
              <img src={titleUnderline} alt="" className={styles.titleUnderline} aria-hidden />
            )}
          </div>
          <SectionSubtitle size="medium" className={styles.subtitle}>{t('FAQ subtitle')}</SectionSubtitle>
        </header>

        <ul className={styles.list}>
          {FAQ_ITEM_IDS.map((id) => {
            const isOpen = openId === id;
            return (
              <li
                key={id}
                className={isProductVariant ? `${styles.item} ${styles.itemProduct}` : styles.item}
                data-open={isOpen || undefined}
              >
                <button
                  type="button"
                  className={isProductVariant ? `${styles.trigger} ${styles.triggerProduct}` : styles.trigger}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggle(id);
                  }}
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${id}`}
                  id={`faq-question-${id}`}
                >
                  <span className={styles.question}>{t(`FAQ item ${id} title`)}</span>
                  <span className={styles.icon} aria-hidden>
                    <PlusIcon />
                  </span>
                </button>
                <div
                  id={`faq-answer-${id}`}
                  className={`${styles.answerWrap} ${isOpen ? styles.answerWrapOpen : ''}`}
                  role="region"
                  aria-labelledby={`faq-question-${id}`}
                  hidden={!isOpen}
                >
                  {id === 2 ? (
                    <ul className={styles.answerList}>
                      {[1, 2, 3, 4, 5, 6].map((step) => (
                        <li key={step}>{t(`FAQ item 2 step ${step}`)}</li>
                      ))}
                    </ul>
                  ) : id === 1 ? (
                    <div className={styles.answerBlock}>
                      <p className={styles.answer}>{t('FAQ item 1 description 1')}</p>
                      <p className={styles.answer}>{t('FAQ item 1 description 2')}</p>
                    </div>
                  ) : (
                    <p className={styles.answer}>
                      {id === 6 ? (
                        <Trans
                          i18nKey="FAQ item 6 description"
                          components={[
                            <a
                              key="email"
                              href="mailto:orders-wildling@repair-rebels.com"
                              className={styles.answerHighlight}
                            />,
                            <span key="disclaimer" className={styles.answerDisclaimer} />,
                          ]}
                        />
                      ) : id === 7 ? (
                        <Trans
                          i18nKey="FAQ item 7 description"
                          components={[
                            <a
                              href="https://www.repair-rebels.com"
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.answerHighlightUnderline}
                            />,
                          ]}
                        />
                      ) : (
                        t(`FAQ item ${id} description`)
                      )}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
